import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { ROLES } from '../utils/constants';

// Auth pages
const Login = React.lazy(() => import('../pages/auth/Login'));
const Signup = React.lazy(() => import('../pages/auth/Signup'));
const ForgotPassword = React.lazy(() => import('../pages/auth/ForgotPassword'));
const ForgotPasswordOtp = React.lazy(() => import('../pages/auth/ForgotPasswordOtp'));

const ForgotPasswordSent = React.lazy(() => import('../pages/auth/ForgotPasswordSent'));
const ResetPassword = React.lazy(() => import('../pages/auth/ResetPassword'));
const ResetPasswordSuccess = React.lazy(() => import('../pages/auth/ResetPasswordSuccess'));
const UpdateLicense = React.lazy(() => import('../pages/auth/UpdateLicense'));
const LicenseSubmittedSuccess = React.lazy(() => import('../pages/auth/LicenseSubmittedSuccess'));
const AccountDeleted = React.lazy(() => import('../pages/auth/AccountDeleted'));
const VerificationResubmit = React.lazy(() => import('../pages/auth/VerificationResubmit'));

// Superadmin pages
const  SuperadminDashboard = React.lazy(() => import('../pages/superadmin/Dashboard'));
const SuperadminApprovals = React.lazy(() => import('../pages/superadmin/Approvals'));
const LicenseManagement = React.lazy(() => import('../pages/superadmin/LicenseManagement'));
const SuperadminContractors = React.lazy(() => import('../pages/superadmin/Contractors'));
const SuperadminEngineers = React.lazy(() => import('../pages/superadmin/Engineers'));
const SuperadminConsultants = React.lazy(() => import('../pages/superadmin/Consultants'));
const SuperadminCustomers = React.lazy(() => import('../pages/superadmin/Customers'));
const FeaturedProviders = React.lazy(() => import('../pages/superadmin/FeaturedProviders'));
const SuperadminQuotes = React.lazy(() => import('../pages/superadmin/Quotes'));
const SuperadminQuotesBookings = React.lazy(() => import('../pages/superadmin/QuotesBookings'));
const SuperadminContentManagement = React.lazy(() => import('../pages/superadmin/ContentManagement'));
const SuperadminAds = React.lazy(() => import('../pages/superadmin/Ads'));
const CreateAdPost = React.lazy(() => import('../pages/superadmin/CreateAdPost'));
const SuperadminOffers = React.lazy(() => import('../pages/superadmin/Offers'));
const SuperadminSettings = React.lazy(() => import('../pages/superadmin/Settings'));
const SuperadminSuppliers = React.lazy(() => import('../pages/superadmin/Suppliers'));
const SuperadminOrders = React.lazy(() => import('../pages/superadmin/Orders'));
const SuperadminOrderDetails = React.lazy(() => import('../pages/superadmin/OrderDetails'));

// Detail pages
const ContractorDetail = React.lazy(() => import('../pages/superadmin/ContractorDetail'));
const SupplierDetail = React.lazy(() => import('../pages/superadmin/SupplierDetail'));
const CustomerDetail = React.lazy(() => import('../pages/superadmin/CustomerDetail'));
const EngineerDetail = React.lazy(() => import('../pages/superadmin/EngineerDetail'));
const AdProjectDetail = React.lazy(() => import('../pages/superadmin/AdProjectDetail'));
const QuoteDetail = React.lazy(() => import('../pages/superadmin/QuoteDetail'));
const QuotesDetail = React.lazy(() => import('../pages/superadmin/QuotesDetail'));
const SuperadminContractorQuotes = React.lazy(() => import('../pages/superadmin/ContractorQuotes'));
const SuperadminCustomerQuotes = React.lazy(() => import('../pages/superadmin/CustomerQuotes'));
const QuotationView = React.lazy(() => import('../pages/superadmin/QuotationView'));
const ProjectDetails = React.lazy(() => import('../pages/superadmin/ProjectDetails'));
const SubmittedQuote = React.lazy(() => import('../pages/superadmin/SubmittedQuote'));

// Contractor pages
const ContractorDashboard = React.lazy(() => import('../pages/contractor/Dashboard'));
const ContractorQuotes = React.lazy(() => import('../pages/contractor/Quotes'));
const ContractorAds = React.lazy(() => import('../pages/contractor/Ads'));
const ContractorProfile = React.lazy(() => import('../pages/contractor/Profile'));
const ContractorInvitations = React.lazy(() => import('../pages/contractor/Invitations'));
const ContractorSubmitQuote = React.lazy(() => import('../pages/contractor/SubmitQuote'));
const ContractorWorkSummary = React.lazy(() => import('../pages/contractor/WorkSummary'));
const QuoteDetails = React.lazy(() => import('../pages/contractor/QuoteDetails'));
const ContractorSupplier = React.lazy(() => import('../pages/contractor/ContractorSupplier'));
const ContractorSupplierDetails = React.lazy(() => import('../pages/contractor/ContractorSupplierDetails'));
const ContractorNewRequest = React.lazy(() => import('../pages/contractor/ContractorNewRequest'));
const ContractorOrders = React.lazy(() => import('../pages/contractor/Orders'));
const ContractorOrderDetails = React.lazy(() => import('../pages/contractor/OrderDetails'));

// Supplier pages
const SupplierDashboard = React.lazy(() => import('../pages/supplier/Dashboard'));
const SupplierRequests = React.lazy(() => import('../pages/supplier/Requests'));
const SupplierQuotations = React.lazy(() => import('../pages/supplier/Quotations'));
const SupplierProfile = React.lazy(() => import('../pages/supplier/Profile'));
const SupplierRequestDetails = React.lazy(() => import('../pages/supplier/RequestDetails'));
const SupplierOrders = React.lazy(() => import('../pages/supplier/Orders'));
const SupplierOrderDetails = React.lazy(() => import('../pages/supplier/OrderDetails'));
const SupplierPayments = React.lazy(() => import('../pages/supplier/SupplierPayments'));

// Payment pages
const PaymentSuccess = React.lazy(() => import('../pages/payment/PaymentSuccess'));
const PaymentFailure = React.lazy(() => import('../pages/payment/PaymentFailure'));

// Engineer pages
const EngineerDashboard = React.lazy(() => import('../pages/engineer/Dashboard'));
const EngineerInspections = React.lazy(() => import('../pages/engineer/Inspections'));
const EngineerQuantifications = React.lazy(() => import('../pages/engineer/Quantifications'));
const EngineerCalendar = React.lazy(() => import('../pages/engineer/Calendar'));
const EngineerWorkSummary = React.lazy(() => import('../pages/engineer/WorkSummary'));
const EngineerProfile = React.lazy(() => import('../pages/engineer/Profile'));
const EngineerInspectionReport = React.lazy(() => import('../pages/engineer/InspectionReport'));
const EngineerQuantificationReport = React.lazy(() => import('../pages/engineer/QuantificationReport'));
const EngineerPayments = React.lazy(() => import('../pages/engineer/Payments'));


// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();



  if (loading) {

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {

    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {

    // Redirect to appropriate dashboard based on user role
    switch (user.role) {
      case ROLES.SUPERADMIN:
        return <Navigate to="/superadmin" replace />;
      case ROLES.CONTRACTOR:
      case ROLES.CONSULTANT:
        return <Navigate to="/service-provider" replace />;
      case ROLES.ENGINEER:
        return <Navigate to="/service-provider" replace />;
      case ROLES.SUPPLIER:
        return <Navigate to="/supplier" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }


  return children;
};

// Public Route Component (for auth pages)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();



  if (loading) {

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (user) {

    // Redirect to appropriate dashboard based on user role
    switch (user.role) {
      case ROLES.SUPERADMIN:
        return <Navigate to="/superadmin" replace />;
      case ROLES.CONTRACTOR:
      case ROLES.CONSULTANT:
        return <Navigate to="/service-provider" replace />;
      case ROLES.ENGINEER:
        return <Navigate to="/service-provider" replace />;
      case ROLES.SUPPLIER:
        return <Navigate to="/supplier" replace />;
      default:
        return <Navigate to="/superadmin" replace />;
    }
  }


  return children;
};

// Role-based Layout Component
const RoleLayout = ({ children, noCard = false }) => {
  return <Layout noCard={noCard}>{children}</Layout>;
};

const AppRoutes = () => {
  return (
    <React.Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      }
    >
      <Routes>
        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
  
        <Route
          path="/forgot-password/otp"
          element={
            <PublicRoute>
              <ForgotPasswordOtp />
            </PublicRoute>
          }
        />
  
        <Route
          path="/forgot-password/sent"
          element={
            <PublicRoute>
              <ForgotPasswordSent />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password/success"
          element={
            <PublicRoute>
              <ResetPasswordSuccess />
            </PublicRoute>
          }
        />
        <Route
          path="/update-license"
          element={
            <PublicRoute>
              <UpdateLicense />
            </PublicRoute>
          }
        />
        <Route
          path="/license-submitted-success"
          element={
            <PublicRoute>
              <LicenseSubmittedSuccess />
            </PublicRoute>
          }
        />
        <Route
          path="/account-deleted"
          element={
            <PublicRoute>
              <AccountDeleted />
            </PublicRoute>
          }
        />
        <Route
          path="/verification-resubmit"
          element={
            <PublicRoute>
              <VerificationResubmit />
            </PublicRoute>
          }
        />

        

        {/* Default redirect */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/superadmin" replace />
            </ProtectedRoute>
          }
        />

        {/* Superadmin Routes */}
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminDashboard />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/approvals"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminApprovals />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/license-management"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <LicenseManagement />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/contractors"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminContractors />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/engineers"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminEngineers />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/consultants"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminConsultants />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/service-provider/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <ContractorDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/service-provider-quotes/:contractorId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminContractorQuotes />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/quotation-view/:quotationId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <QuotationView />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/project-details/:quoteId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <ProjectDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/submitted-quote/:quotationId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SubmittedQuote />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/engineer-detail/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <EngineerDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/customers"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminCustomers />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/suppliers"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminSuppliers />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/supplier-detail/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SupplierDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/featured-providers"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <FeaturedProviders />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/customer-detail/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout noCard>
                <CustomerDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/customer-quotes/:customerId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminCustomerQuotes />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/quotes"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminQuotes />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/quotes-bookings"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminQuotesBookings />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/ads"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout  noCard={true}>
                <SuperadminAds />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/ads/create"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout noCard={true}>
                <CreateAdPost />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/offers"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout noCard={true}>
                <SuperadminOffers />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
                <Route
          path="/superadmin/ContentManagement"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout >
                <SuperadminContentManagement />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/settings"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminSettings />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        {/* Detail Page Routes */}
        <Route
          path="/superadmin/contractors/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <ContractorDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/engineers/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <EngineerDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/ads/:projectId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout noCard={true}>
                <AdProjectDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/quotes/:quoteId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <QuoteDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/quotes-detail/:quoteId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout noCard={true}>
                <QuotesDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/orders"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout>
                <SuperadminOrders />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/orders/:orderId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPERADMIN]}>
              <RoleLayout noCard={true}>
                <SuperadminOrderDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        {/* Service Provider Routes */}
        <Route
          path="/service-provider"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.ENGINEER]}>
              <RoleLayout>
                <ContractorDashboard />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/quotes"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.ENGINEER]}>
              <RoleLayout noCard={true}>
                <ContractorQuotes />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/ads/:postId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.SUPPLIER, ROLES.ENGINEER]}>
              <RoleLayout noCard={true}>
                <AdProjectDetail />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/ads"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.ENGINEER]}>
              <RoleLayout noCard={true}>
                <ContractorAds />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/quote-details/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.SUPPLIER, ROLES.ENGINEER]}>
              <RoleLayout noCard={true}>
                <QuoteDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/service-provider/profile"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.ENGINEER]}>
              <RoleLayout noCard={true}>
                <ContractorProfile />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/invitations"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.ENGINEER]}>
              <RoleLayout noCard={true}>
                <ContractorInvitations />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/submit-quote/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.SUPPLIER]}>
              <RoleLayout>
                <ContractorSubmitQuote />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/work-summary"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR, ROLES.CONSULTANT, ROLES.ENGINEER]}>
              <RoleLayout>
                <ContractorWorkSummary />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/payments"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerPayments />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/service-provider/supplier"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR]}>
              <RoleLayout noCard={true}>
                <ContractorSupplier />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/supplier/:projectId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR]}>
              <RoleLayout noCard={true}>
                <ContractorSupplierDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-provider/supplier/new-request"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR]}>
              <RoleLayout noCard={true}>
                <ContractorNewRequest />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        {/* Engineer Routes */}
        <Route
          path="/engineer"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerDashboard />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer/inspections"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerInspections />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer/quantifications"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerQuantifications />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer/calendar"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerCalendar />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer/work-summary"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerWorkSummary />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer/profile"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerProfile />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer/inspection-report/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerInspectionReport />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engineer/quantification-report/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.ENGINEER]}>
              <RoleLayout>
                <EngineerQuantificationReport />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        {/* Supplier Routes */}
        <Route
          path="/supplier"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout>
                <SupplierDashboard />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/requests"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout>
                <SupplierRequests />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/requests/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout noCard={true}>
                <SupplierRequestDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/quotations"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout>
                <SupplierQuotations />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/orders"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout>
                <SupplierOrders />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/orders/:id"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout>
                <SupplierOrderDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/payments"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout>
                <SupplierPayments />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/profile"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout>
                <SupplierProfile />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/supplier/suppliers/new-request"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout noCard={true}>
                <ContractorNewRequest />
              </RoleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier/suppliers/:projectId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.SUPPLIER]}>
              <RoleLayout noCard={true}>
                <ContractorSupplierDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        {/* Contractor Orders */}
        <Route
          path="/service-provider/orders"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR]}>
              <RoleLayout noCard={true}>
                <ContractorOrders />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/service-provider/orders/:orderId"
          element={
            <ProtectedRoute allowedRoles={[ROLES.CONTRACTOR]}>
              <RoleLayout noCard={true}>
                <ContractorOrderDetails />
              </RoleLayout>
            </ProtectedRoute>
          }
        />

        {/* Payment Result Routes */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failure" element={<PaymentFailure />} />

        {/* Catch all route */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Navigate to="/superadmin" replace />
            </ProtectedRoute>
          }
        />
      </Routes>
    </React.Suspense>
  );
};

export default AppRoutes; 